import { type IRentalDispense } from "@/interfaces/api/rental-dispense.interface"
import { RentalDispenseItem } from "@/models/rental-dispense-item.model"
import type { RentalIntake } from "@/models/rental-intake.model"
import { allArrayElementsEqual } from "@/helper/collection-helper"
import { ModelFactory } from "@/factories/model-factory"
import { RentalMutation } from "@/models/rental-mutation.model"
import { ShallowRentalDispense } from "@/models/shallow-rental-dispense.model"
import { RentalMutationFlowDirections } from "@/enums/rental-mutation-flow-directions"

export class RentalDispense extends RentalMutation implements IRentalDispense {
  public static className = "RentalDispense" // Used for socket communication.
  public static verboseName = "Verhuurmutatie"

  rentalDispenseItem: RentalDispenseItem
  counterMutation?: RentalDispense
  counteredBy: ShallowRentalDispense[]
  productionStatement?: string
  productionStatementCreationJob?: string
  productionStatementReference?: string
  supplierComment: string

  constructor(rentalDispense: IRentalDispense, managed = false) {
    super(rentalDispense, managed)

    this.rentalDispenseItem = ModelFactory.childModel(
      RentalDispenseItem,
      rentalDispense.rentalDispenseItem,
      managed
    )
    this.counterMutation = ModelFactory.childModel(
      RentalDispense,
      rentalDispense.counterMutation,
      managed
    )

    // ShallowRentalDispense is a shallow version of RentalDispense.
    this.counteredBy = ModelFactory.childModels(
      ShallowRentalDispense,
      rentalDispense.counteredBy,
      managed
    )
    this.productionStatement = rentalDispense.productionStatement
    this.productionStatementCreationJob = rentalDispense.productionStatementCreationJob
    this.productionStatementReference = rentalDispense.productionStatementReference

    this.supplierComment = rentalDispense.supplierComment
  }

  /** Locked while linked to a production statement or reserved by a creation job generating one. */
  public isLocked(): boolean {
    return Boolean(this.productionStatement || this.productionStatementCreationJob)
  }

  public matchesRentalIntake(rentalIntake: RentalIntake) {
    // Perform basic matching checks
    const matchesBasic =
      rentalIntake.flowDirection === this.flowDirection && // Same flow direction
      rentalIntake.rentalIntakeItem?.rentalDispenseItem?.id === this.rentalDispenseItem?.id && // Same product
      ((!rentalIntake.quantity && !this.quantity) || // Both have no quantity (e.g., when creating new mutations)
        Number(rentalIntake.quantity) === Number(this.quantity)) && // Same quantity
      allArrayElementsEqual(
        rentalIntake.fileBucket.fileRelations,
        this.fileBucket.fileRelations,
        "file.id"
      ) // Same related files

    if (!matchesBasic) {
      return false
    }

    if (rentalIntake.flowDirection !== RentalMutationFlowDirections.ISSUANCE) {
      // In addition to the basic checks: one can only create a rental intake and dispense return pair that matches
      // the rental intake and dispense issuance pair.
      return this.counterMutation?.id === rentalIntake.counterMutation!.associatedRentalDispense!.id
    }

    return true
  }

  public getItem() {
    return this.rentalDispenseItem
  }
}
